/*
 * IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA
 * GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT
 *
 * Copyright: 2023 by Idemia Identity & Security USA LLC. All rights reserved.
 * License: In accordance  Idemia I&S USA LLC's license agreement.
 * Code Classification: GOVERNMENT
 *
 * Classification Person: Nadim Bakizada nadim.bakizada@us.idemia.com
 * Classification Reason: Software not specific to any U.S. Government Entity
 * Classification Date: 2023
 *
 * GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT
 * IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA
 */

import React, { useState, useContext, useCallback, useMemo, useEffect } from "react";
import darkTheme from './darkTheme';
import lightTheme from './lightTheme';
import { ThemeProvider, createTheme, Box, useTheme, PaletteOptions } from '@mui/material';
import { light } from "@mui/material/styles/createPalette";
import { ThemeStorage } from '../hooks/shared/helpers/themeStorage';
import { useUserStore } from '../state';

declare module '@mui/material/styles' {
  interface Theme {
    appBar: {
      iconColor: string;
    }
  }
  interface ThemeOptions {
    appBar?: {
      iconColor?: string;
    };
  }
}

export enum Mode {
  LIGHT = "light",
  DARK = "dark",
}

type ThemeContextType = {
  mode: Mode;
  toggleTheme: () => void;
  setThemeFromLogin: (newMode: Mode) => void;
  resetTheme: (newMode: Mode) => void;
  saveTheme: (newTheme: Mode | string, username?: string) => void;
  loadUserTheme: (username: string) => string;
};

const prefersDarkMode = window?.matchMedia("(prefers-color-scheme: dark)").matches;
const DEFAULT_THEME = prefersDarkMode ? Mode.DARK : Mode.LIGHT;

const ThemeContext = React.createContext<ThemeContextType>({
  mode: Mode.DARK,
  toggleTheme: () => { },
  setThemeFromLogin: () => { },
  resetTheme: () => { },
  saveTheme: () => { },
  loadUserTheme: () => DEFAULT_THEME
});

const THEMES = {
  light: lightTheme,
  dark: darkTheme
}

const useMUITheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useMUITheme must be used within a ThemeProvider_MUI");
  return context;
};

type ThemeProviderProps = {
  children: React.ReactNode;
};

const ThemeProvider_MUI = ({ children }: ThemeProviderProps) => {
  const [mode, setTheme] = useState<Mode>(() => {
    const storedTheme = localStorage.getItem("theme");
    if (storedTheme && (storedTheme === Mode.LIGHT || storedTheme === Mode.DARK)) {
      return storedTheme as Mode;
    }

    return Mode.LIGHT;
  })

  const persistTheme = useCallback((newMode: Mode) => {
    localStorage.setItem("theme", newMode.toString());
  }, []);

  useEffect(() => {
    if (mode === Mode.DARK) {
      document.body.classList.add("dark");
      document.body.classList.remove("light");
    } else {
      document.body.classList.add("light");
      document.body.classList.remove("dark");
    }
  }, [mode]);

  const toggleTheme = useCallback(() => {
    const newMode = mode === Mode.LIGHT ? Mode.DARK : Mode.LIGHT;
    const newModeString = newMode === Mode.DARK ? 'dark' : 'light';

    setTheme(newMode);
    persistTheme(newMode);

    const userConfig = useUserStore.getState().userConfig;
    const username = userConfig?.username;

    if (username && userConfig.loggedIn) {
      ThemeStorage.save(newModeString, username);
    } else {
      localStorage.setItem("pendingThemeSave", newModeString);
      console.log(`Theme changed pre-login to ${newModeString}, marking as pending`);
    }
  }, [mode, persistTheme]);

  const setThemeFromLogin = useCallback((newMode: Mode) => {
    setTheme(newMode);
  }, []);

  const resetTheme = useCallback((newMode: Mode) => {
    setTheme(newMode);
    persistTheme(newMode);
  }, [persistTheme]);

  const saveTheme = useCallback((newTheme: Mode | string, username?: string) => {
    console.log(`ThemeProvider.saveTheme called with: ${newTheme}, username: ${username}`)
    console.log('ThemeProvider.saveTheme stack trace:', new Error().stack);

    let themeString: string;
    let themeMode: Mode;

    if (typeof newTheme === "string") {
      themeString = newTheme;
      themeMode = newTheme === 'dark' ? Mode.DARK : Mode.LIGHT;
    } else {
      themeMode = newTheme;
      themeString = newTheme === Mode.DARK ? 'dark' : 'light';
    }

    console.log(`ThemeProvider.saveTheme setting theme to: ${themeString}`);
    setTheme(themeMode);

    if (username) {
      ThemeStorage.save(themeString, username);
    } else {
      localStorage.setItem("theme", themeString);
    }

    const userConfig = useUserStore.getState().userConfig;
    if (userConfig.loggedIn && username) {
      const setUserConfig = useUserStore.getState().setUserConfig;
      setUserConfig({
        ...userConfig,
        theme: themeString
      });
    }
  }, []);

  const loadUserTheme = useCallback((username: string) => {
    const userTheme = ThemeStorage.load(username);
    if (userTheme && userTheme !== 'undefined') {
      const themeMode = userTheme === 'dark' ? Mode.DARK : Mode.LIGHT;
      setTheme(themeMode);
      localStorage.setItem('theme', userTheme);
      return userTheme;
    }

    return mode;
  }, [mode]);

  const contextValue = useMemo(() => ({
    mode,
    toggleTheme,
    setThemeFromLogin,
    resetTheme,
    saveTheme,
    loadUserTheme
  }), [mode, toggleTheme, setThemeFromLogin, resetTheme, saveTheme, loadUserTheme]);

  return (
    <ThemeProvider theme={mode === Mode.DARK ? darkTheme : lightTheme}>
      <ThemeContext.Provider value={contextValue}>
        {children}
      </ThemeContext.Provider>
    </ThemeProvider>
  );
};

export { ThemeProvider_MUI, useMUITheme };